import {ChangeDetectionStrategy, Component, DestroyRef, input, OnInit, viewChild, viewChildren} from '@angular/core'
import {GoogleMap, MapInfoWindow, MapMarker, MapMarkerClusterer} from '@angular/google-maps'
import {GoogleMapsService} from '../../../core/geo/google-maps.service'
import {AsyncPipe, NgOptimizedImage} from '@angular/common'
import {ProMapLocation, proMapLocationFrom} from './pro-map.types'
import {Region} from '../../../core/content/types/craftsmen-region-types'
import {ProDirectory_SearchGql} from '../../../../__generated__/types'
import {Observable} from 'rxjs'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {map} from 'rxjs/operators'
import {proDirectoryGqlToProDirectory} from '../pro-directory/pro.directory.types'
import {PUBLIC_ROUTES} from '../../routing/public.routes'
import {LinkService} from '../../../core/i18n/link.service'
import {RouterLink} from '@angular/router'
import {TranslocoModule} from '@ngneat/transloco'
import '@googlemaps/markerclustererplus'

@Component({
  selector: 'bgo-pro-map',
  templateUrl: 'pro-map.component.html',
  styles: `
    :host {
      display: block;
    }
  `,
  imports: [
    GoogleMap,
    MapMarkerClusterer,
    AsyncPipe,
    MapMarker,
    MapInfoWindow,
    RouterLink,
    TranslocoModule,
    NgOptimizedImage,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProMapComponent implements OnInit {
  sectionId = input<string>()
  region = input.required<Region>()
  industryIds = input<number[]>()
  title = input<string>()

  protected readonly googleMapsApiLoaded$ = this.googleMapsService.apiLoaded$
  protected locations$!: Observable<ProMapLocation[]>
  protected readonly markerClustererImagePath = '/assets/images/google-maps/markerclusterer/m'

  private map = viewChild(GoogleMap)
  private infoWindows = viewChildren(MapInfoWindow)
  private markers = viewChildren(MapMarker)

  constructor(
    private googleMapsService: GoogleMapsService,
    private proDirectoryGql: ProDirectory_SearchGql,
    private linkService: LinkService,
    private destroyRef: DestroyRef,
  ) {}

  ngOnInit() {
    this.locations$ = this.proDirectoryGql
      .fetch({
        criteria: {
          industryIds: this.industryIds()?.map(id => id.toString()),
          region: {
            postalCodes: this.region().postalCodes,
          },
        },
        page: {
          limit: 25,
          offset: 0,
        },
      })
      .pipe(
        map(result => {
          return result.data.searchProDirectory.items
            .map(proDirectoryGqlToProDirectory)
            .map(pro => {
              return pro.locations.map(location => proMapLocationFrom(pro, location))
            })
            .flat()
            .filter(location => this.region().postalCodes.includes(location.postalCode) && location.geoPoint)
        }),
        takeUntilDestroyed(this.destroyRef),
      )
  }

  showInfoWindow(event: google.maps.MapMouseEvent, i) {
    this.infoWindows().forEach(infoWindow => {
      infoWindow.close()
    })
    this.infoWindows()[i].open(this.markers()[i])
  }

  setBounds(locations: ProMapLocation[]) {
    // Make sure the markers are all in the viewport of the map
    const bounds = new google.maps.LatLngBounds()
    locations.forEach(l => bounds.extend(l.geoPoint!))
    this.map()?.fitBounds(bounds)
  }

  proProfileRoute(slug: string) {
    return this.linkService.localizedPathTo(PUBLIC_ROUTES.PRO_PROFILE, {
      routeParams: {
        slug,
      },
    })
  }
}
